import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported, Messaging } from 'firebase/messaging';

// Your Firebase configuration
// Replace these values with your actual Firebase project configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

let messaging: Messaging | null = null;

const canUseMessaging = async (): Promise<boolean> => {
  if (typeof window === 'undefined') return false;
  if (!('serviceWorker' in navigator)) return false;
  if (!('Notification' in window)) return false;
  try {
    return await isSupported();
  } catch {
    return false;
  }
};

// Request permission and get token
export const requestNotificationPermission = async () => {
  try {
    const supported = await canUseMessaging();
    if (!supported) {
      console.warn('Firebase Messaging not supported in this browser/context.');
      return null;
    }

    if (!messaging) {
      // Ensure service worker is registered before requesting token
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      messaging = getMessaging(app);
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('Notification permission denied');
        return null;
      }
      const token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration,
      });
      console.log('Notification permission granted. Token:', token);
      return token;
    }

    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      const registration = await navigator.serviceWorker.ready;
      const token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration,
      });
      console.log('Notification permission granted. Token:', token);
      return token;
    } else {
      console.log('Notification permission denied');
      return null;
    }
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return null;
  }
};

// Handle foreground messages
export const onMessageListener = async () => {
  const supported = await canUseMessaging();
  if (!supported) return Promise.resolve(null);
  if (!messaging) {
    try {
      await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      messaging = getMessaging(app);
    } catch (e) {
      console.error('Failed to initialize messaging for onMessage:', e);
      return Promise.resolve(null);
    }
  }
  return new Promise((resolve) => {
    onMessage(messaging as Messaging, (payload) => {
      console.log('Message received in foreground:', payload);
      resolve(payload);
    });
  });
};

export { messaging };